import type { MarketplaceKey } from "@porsche-customer/slfinrtl-context-types"
import type { Environment } from "./Environment"
import type { FulfillmentSalesEnabled, MarketplaceConfig, OfferMode } from "./MarketplaceConfig"
import { analyticsEnabledMarketplaces } from "./analyticsEnabledMarketplaces"
import { getExtraCostVehicleConditionMode } from "./getExtraCostVehicleConditionMode"
import { getExtraCostsSettingsInfoTextMode } from "./getExtraCostsSettingsInfoTextMode"
import { getFinancingProducts } from "./getFinancingProducts"
import { getMinimumSubsidizedUsageTime } from "./getMinimumSubsidizedUsageTime"
import { salesPricesRestrictedMarketplaces } from "./salesPricesRestrictedMarketplaces"

interface MarketplaceEnvSetting {
	env: Environment[]
	key: MarketplaceKey
}

type MarketplaceSetting = MarketplaceKey | MarketplaceEnvSetting

export const getMarketplaceConfig = (key: MarketplaceKey, env: Environment): MarketplaceConfig => {
	const every = getEvery(key, env)
	const except = getExcept(key, env)
	const fulfillmentSalesEnabled = getFulfillmentSalesEnabled(key, env)
	const offerMode = getOfferMode(key, env)

	return {
		accidentFreeLabel: every(["de", "xp"]),
		aiListingDescriptionEnabled: every([{ env: ["dev", "preview"], key: "de" }]),
		allVehicleTypesAvailable: except(["au", "us"]),
		analyticsEnabled: every(analyticsEnabledMarketplaces),
		bpIdInputForEndCustomers: every(["au"]),
		buildsheetsEnabled: every(["us", "ca"]),
		capIntegrationEnabled: except(["dk", "fi", "is"]),
		carfaxAvailable: every(["us"]),
		carPassEnabled: every(["be"]),
		carSalesLiteEnabled: every(["dk", "fi", "is"]),
		chPartnerPortalLink: every(["ch"]),
		collectBPIdForResellers: except(["us", "it", ...latinAmerica]),
		contractualAgreementsEnabled: every(["de", "ch", "fr"]),
		dealerFeesAndAccessoriesEnabled: every(["ca"]),
		dealerInitiatedSalesWorkflowEnabled: true,
		displayedMarketplacesDeliveryChecklist: every(["de", "ch", "xp"]),
		editListingPriceNewCars: except(["us"]),
		extraCostsAllowEditSettings: every(["de", "ch", "fr", "us"]),
		extraCostsEnabled: every(["au", "de", "ch", "fr", "nl", "us"]),
		extraCostsNewTag: every(["us"]),
		extraCostsSettingsInfoTextMode: getExtraCostsSettingsInfoTextMode(key),
		extraCostsSettingsVehicleConditionMode: getExtraCostVehicleConditionMode(key),
		financingProducts: getFinancingProducts(key),
		firstRegistrationDateAvailable: except(["us", "ca"]),
		fulfillmentDeliveryChecklistEnabled: every(fulfillmentDeliveryChecklistMarkets),
		fulfillmentSalesEnabled,
		inquiryContextCdpEnabled: every(["de"]),
		intakeTypeWerkswagenAvailable: except(["us"]),
		inventoryIntegrationMonitorEnabled: every(["us"]),
		isDosEnabled: every(["de", "fr", "ch"]),
		isEuMarket: every(euMarkets),
		isOneVMSMarket: every([
			// devOneVmsMarkets in the dev environment
			...devOnlyOneVmsMarkets.map(
				(marketplaceKey): MarketplaceEnvSetting => ({ env: ["dev"], key: marketplaceKey }),
			),
			// OneVMS markets in all environments
			"ar",
			"cl",
			"co",
			"do",
			"ec",
			"gp",
			"gt",
			"jm",
			"mx",
			"pa",
			"pe",
			"pl",
			"pr",
			"py",
			"sv",
			"tt",
			"uy",
		]),
		isPVMSMarket: every(vmsMarkets),
		isSyPNationwideMarket: every(["au", "ch", "de", "fr", "gb", "it"]),
		licensePlateRequired: every(["gb", "ie"]),
		listingAccidentDescriptionEnabled: except(["gb"]),
		marketDeliveredExportEnabled:
			!salesPricesRestrictedMarketplaces.includes(key) &&
			(env === "prod" ? every([]) : except(["gb", "de", "fr"])),
		marketInventoryExportEnabled: env === "prod" ? every(["us"]) : except(["gb", "de", "fi"]),
		marketRolesCanViewSalesPrices: except(salesPricesRestrictedMarketplaces),
		masterNumberChAvailable: every(["ch"]),
		monroneyLabelEnabled: every(["us"]),
		notificationCenterEnabled:
			env === "dev" || env === "preview"
				? true
				: every([
						"ar",
						"cl",
						"co",
						"do",
						"ec",
						"gp",
						"gt",
						"hr",
						"jm",
						"mx",
						"pa",
						"pe",
						"pl",
						"pr",
						"py",
						"sk",
						"sv",
						"tt",
						"uy",
					]),
		offers: {
			createExposeEnabled: env !== "prod" || every(["pl", ...latinAmerica]) || offerMode === "FULL",
			exposeTyreLabelsAvailable: every(tyreLabelEnabledMarkets),
			offerMode,
			salesReportingEnabled: fulfillmentSalesEnabled === "enabled",
			showPvmsOffers: fulfillmentSalesEnabled === "enabled",
			transferOfferEnabled: every(pmeMarkets),
			useConfigurationDetailsPage: env === "prod" ? every(["pl", ...latinAmerica, ...pmeMarkets]) : true,
		},
		onlineOrderingEnabled: every(onlineOrderingEnabledMarkets),
		onlyImagesAvailable: every(["cn"]),
		pcwAnalyticsEnabled: every(["us"]),
		pfcLinkAllVehicleTypes: every(["de", "ch", "it", "fr"]),
		pfcLinkDisabledCommissionCars: every(["de"]),
		pfcLinkEnabled: every(["de", "ch", "it", "fr", { env: ["dev", "preview"], key: "jp" }]),
		porscheApprovedEnabled: every(porscheApprovedMarkets),
		preProductionVehiclesEnabled: every(preProductionVehiclesEnabledMarkets),
		previousOwnersRequired: except(["ch", "us", "za"]),
		previousOwnerUsageHistoryEnabled: every(["jp"]),
		priceNetAvailable: every(["au"]),
		print: {
			energyEfficiencyLabelEnabled: every(["de", "xp"]),
			exposeEnabled: except(["nl"]),
			financialServices: {
				exposeFsOfferEnabled: every(["ch", { env: ["dev", "preview"], key: "fr" }]),
				priceTagFsOfferEnabled: every(["ch", "de", { env: ["dev", "preview"], key: "fr" }]),
			},
			priceTagEnabled: except(["ca"]),
			tyreLabel: {
				enabled: every(tyreLabelEnabledMarkets),
				link: "https://tyre.vehicle.finder.porsche.com/{vin}/pdf?languageTag={languageTag}",
			},
		},
		purchaseRequestsEnabled: every(["au", "ch", "de", "fr", "gb", "it", "no", "pl", "us"]),
		purchaseRequestsFirstRegistrationEnabled: except(["fr", "gb", "it", "us"]),
		showMsrpPrice: every(["de"]),
		showThirdPartyMarketProvider: every(["de"]),
		subsidizedUsage: {
			endUsageDespiteUnsufficientUsageTimePossible: every(["us", "au"]),
			minimumSubsidizedUsageTime: getMinimumSubsidizedUsageTime(key),
		},
		tradeInEnabled: every(["ch", "de", "gb", "it", "ca", "au"]),
		transferEnabled: except(["us"]),
		vatDeductibleAvailable: except(["us"]),
		vehicleInformationEnabled: every(["us"]),
		videoHintEnabled: false,
		whatsNewEnabled: env === "prod" ? every(["hr", "sk"]) : true,
	}
}

const isIncluded = (markets: MarketplaceSetting[], key: MarketplaceKey, env: Environment) => {
	return markets.some((market) => {
		return market === key || (typeof market === "object" && market.key === key && market.env.includes(env))
	})
}

const getEvery = (key: MarketplaceKey, env: Environment) => (markets: MarketplaceSetting[]) =>
	isIncluded(markets, key, env)

const getExcept = (key: MarketplaceKey, env: Environment) => (markets: MarketplaceSetting[]) =>
	!isIncluded(markets, key, env)

const getOfferMode = (key: MarketplaceKey, env: Environment): OfferMode => {
	const every = getEvery(key, env)
	const isFullOfferModeEnabled = every([
		{ env: ["dev"], key: "de" },
		{ env: ["dev", "preview"], key: "sg" },
	])
	return isFullOfferModeEnabled ? "FULL" : "SIMPLIFIED"
}

const getFulfillmentSalesEnabled = (key: MarketplaceKey, env: Environment): FulfillmentSalesEnabled => {
	const every = getEvery(key, env)

	if (env === "dev") {
		return every([...pmeMarkets, ...latinAmerica, ...papMarkets, "pl", "dk", "fi", "is"]) ? "enabled" : "disabled"
	}

	if (env === "preview") {
		return "read-only"
	}

	if (env === "prod") {
		return every(["pl", ...latinAmerica]) ? "enabled" : "disabled"
	}

	return "disabled"
}

// European Union member states
const euMarkets: MarketplaceKey[] = [
	"at", // Austria
	"be", // Belgium
	"bg", // Bulgaria
	"cy", // Cyprus
	"cz", // Czech Republic
	"de", // Germany
	"dk", // Denmark
	"ee", // Estonia
	"es", // Spain
	"fi", // Finland
	"fr", // France
	"gr", // Greece
	"hr", // Croatia
	"hu", // Hungary
	"ie", // Ireland
	"it", // Italy
	"lt", // Lithuania
	"lu", // Luxembourg
	"lv", // Latvia
	"mt", // Malta
	"nl", // Netherlands
	"pl", // Poland
	"pt", // Portugal
	"ro", // Romania
	"si", // Slovenia
	"sk", // Slovakia
]

const tyreLabelEnabledMarkets: MarketplaceKey[] = [
	"at",
	"be",
	"bg",
	"ch",
	"cy",
	"cz",
	"de",
	"ee",
	"es",
	"fr",
	"gb",
	"gp",
	"gr",
	"hr",
	"hu",
	"ie",
	"it",
	"lt",
	"lu",
	"lv",
	"mt",
	"nl",
	"no",
	"pl",
	"pt",
	"re",
	"ro",
	"si",
	"sk",
	"tr",
]

const onlineOrderingEnabledMarkets: MarketplaceKey[] = [
	"au",
	"de",
	"es",
	"fr",
	"gb",
	"it",
	"jp",
	"pt",
	"tw",
	"xa",
	"xd",
	"za",
]

const pceeMarkets: MarketplaceKey[] = [
	"am", // AM - Armenia
	"az", // AS - Azerbaijan
	"ba", // BOS - Bosnia
	"bg", // BG - Bulgaria
	"cy", // CN - Cyprus
	"cz", // XP - Czech Republic
	"ee", // EST - Estonia
	"ge", // GEO - Georgia
	"gr", // GRI - Greece
	"hr", // KRO - Croatia
	"hu", // UN - Hungary
	"il", // ISR - Israel
	"kz", // CEC - Kazakhstan
	"lt", // LIT - Lithuania
	"lv", // LET - Latvia
	"md", // MOL - Moldova
	"mk", // MAC - Macedonia
	"mt", // MA - Malta
	"ro", // RO - Romania
	"rs", // CS - Serbia
	"si", // SO - Slovenia
	"sk", // SL - Slovakia
	"tr", // TM - Turkey
	"ua", // UK - Ukraine
	"uz", // CEU - Uzbekistan
]

const preProductionVehiclesEnabledMarkets: MarketplaceSetting[] = [
	...pceeMarkets,
	"ar",
	"au",
	"ca",
	"ch",
	"cl",
	"cn",
	"co",
	"de",
	"do",
	"ec",
	"eg",
	{ env: ["dev"], key: "fi" }, // enabled for OneVMS integration test
	"fr",
	"gp",
	"gt",
	{ env: ["dev"], key: "in" }, // enabled for OneVMS integration test
	"it",
	"jm",
	"jo",
	"kw",
	"lb",
	"lu",
	"ma",
	"mu",
	"mx",
	{ env: ["dev"], key: "my" }, // enabled for OneVMS integration test
	"no",
	"nz",
	"om",
	"pa",
	"pe",
	"pl",
	"pr",
	"py",
	"qa",
	"re",
	{ env: ["dev"], key: "sg" }, // enabled for OneVMS integration test
	"sv",
	"tn",
	"tt",
	"tw",
	"us",
	"uy",
	{ env: ["dev"], key: "vn" }, // enabled for OneVMS integration test
	"xa",
	"xd",
	"za",
]

const vmsMarkets: MarketplaceSetting[] = [
	...pceeMarkets,
	"ar", // PLR - Argentina
	"at", // AT - Austria
	"au", // AC - Australien
	"be", // BL - Belgium
	"bh", // BDB - Bahrain
	"br", // PBR - Brazil
	"ca", // USC - Canada
	"ch", // CH - Schweiz
	"cl", // LAW/PLC - Chile
	"cn", // PCN - China
	"co", // LAE - Colombia
	// Disabled in de on dev to allow adding new cars (by disabling isNewCarWarrantyCheckRequired)
	{ env: ["preview", "prod"], key: "de" }, // DE - Germany
	"do", // LAD - Dominican Republic
	"ec", // PLE - Ecuador
	"eg", // PDE - Egypt
	"fr", // FR - France
	"gb", // EG - United Kingdom (Great Britain)
	"gp", // LAF - Guadeloupe
	"gt", // LAG - Guatemala
	"hk", // HK - Hong Kong
	"ie", // IRL - Ireland
	"in", // MEE/MEI - India
	"it", // JP - Italy
	"jm", // LAQ - Jamaica
	"jo", // PDJ - Jordanien
	"kr", // PKO - South Korea
	"kw", // PDK - Kuwait
	"lb", // PDL - Libanon
	"lu", // LU - Luxembourg
	"ma", // MEM - Morocco
	"mu", // MEU - Mauritius
	"mx", // PLM - Mexico
	"no", // NW - Norway
	"nz", // PAX - New Zealand
	"om", // PDO - Oman
	"pa", // PLP - Panama
	"pe", // PLF - Peru
	"pl", // PE - Poland
	"pr", // PLT/LAP Puerto Rico
	"py", // PLY - Paraguay
	"qa", // PDT - Qatar
	"re", // MER - La Réunion
	"sa", // RDS - Saudi Arabia
	"sg", // SG - Singapore
	"sv", // LAS - El Salvador
	"tn", // MEN - Tunisia
	"tt", // LAJ - Trinidad and Tobago
	"tw", // PTW - Taiwan
	"us", // USM - USA
	"uy", // LAX - Uruguay
	"vn", // PAT - Vietnam
	"xa", // Abu Dhabi
	"xd", // Dubai
	"za", // PDR - South Africa
]

const devOnlyOneVmsMarkets: MarketplaceKey[] = ["dk", "fi", "in", "is", "ma", "my", "sg", "vn", "xa"]

const porscheApprovedMarkets: MarketplaceSetting[] = [
	...pceeMarkets,
	"ar",
	"au",
	"bh",
	"bn",
	"bo",
	"ca",
	"ch",
	"cl",
	"co",
	"cr",
	"de",
	"do",
	"ec",
	"fr",
	"gb",
	"gp",
	"gt",
	"hk",
	"hn",
	"id",
	"ie",
	"in",
	"is",
	"it",
	"jo",
	"jp",
	"kr",
	"kw",
	"lb",
	"lk",
	"ma",
	"mn",
	"mu",
	"mx",
	"my",
	"nc",
	"no",
	"nz",
	"om",
	"pa",
	"pe",
	"pf",
	"ph",
	"pr",
	"py",
	"qa",
	"sa",
	"sg",
	"sv",
	"th",
	"tt",
	"us",
	"uy",
	"vn",
	"xa",
	"xd",
	"xp",
	"za",
]

const latinAmerica: MarketplaceKey[] = [
	"ar",
	"bo",
	"cl",
	"co",
	"cr",
	"cw",
	"do",
	"ec",
	"gp",
	"gt",
	"hn",
	"jm",
	"mx",
	"pa",
	"pe",
	"pr",
	"py",
	"sv",
	"tt",
	"uy",
]

const fulfillmentDeliveryChecklistMarkets: MarketplaceKey[] = [
	"au", // Australia
	"bh", // Bahrain
	"br", // Brazil
	"ch", // Switzerland
	"cl", // Chile
	"co", // Colombia
	"cr", // Costa Rica
	"de", // Germany
	"eg", // Egypt
	"es", // Spain
	"fr", // France
	"in", // India
	"it", // Italy
	"jo", // Jordan
	"jp", // Japan
	"kw", // Kuwait
	"lb", // Lebanon
	"lu", // Luxembourg
	"ma", // Morocco
	"mx", // Mexico
	"my", // Malaysia
	"no", // Norway
	"om", // Oman
	"pa", // Panama
	"pl", // Poland
	"pr", // Puerto Rico
	"pt", // Portugal
	"qa", // Qatar
	"sa", // Saudi Arabia
	"xa", // Abu Dhabi
	"xd", // Dubai
	"za", // South Africa
]

// Porsche Middle East markets
const pmeMarkets: MarketplaceKey[] = [
	"bh", // Bahrain
	"eg", // Egypt
	"in", // India
	"jo", // Jordan
	"kw", // Kuwait
	"lb", // Lebanon
	"ma", // Morocco
	"mu", // Mauritius
	"om", // Oman
	"qa", // Qatar
	"re", // La Réunion
	"sa", // Saudi Arabia
	"tn", // Tunisia
	"xa", // Abu Dhabi
	"xd", // Dubai
	"za", // South Africa
]

// Porsche Asia Pacific markets (PAP).
const papMarkets: MarketplaceKey[] = [
	"bn", // Brunei
	"id", // Indonesia
	"lk", // Sri Lanka
	"my", // Malaysia
	"nc", // New Caledonia
	"nz", // New Zealand
	"pf", // French Polynesia
	"ph", // Philippines
	"sg", // Singapore
	"th", // Thailand
	"vn", // Vietnam
]
